import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useHistory } from 'react-router';
import { Waypoint } from 'react-waypoint';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useSelectionState } from '../../../../hooks/useSelectionState';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	invalidateAllLegalMatterFilters,
	invalidateAllLegalMatterSummary,
	useExportLegalMattersMutation,
	useGetExternalIntegrationsQuery,
	useLegalMatterContactsMutation,
	useLegalMatterFilter,
	useLegalMatterSummaryQuery,
} from '../../../../queries';
import { BreadcrumbBar } from '../../../components-v2/BreadcrumbBar';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import { Column, Page } from '../../../components-v2/PageLayout';
import { Checkbox } from '../../../components/Checkbox';
import { EntityChip } from '../../../components/chips/EntityChip';
import {
	Databoard,
	DataboardActions,
	DataboardBody,
	DataboardFilters,
	DataboardNoResults,
	DataboardStat,
	DataboardStatLabel,
	DataboardStats,
	DataboardStatTitle,
	DataboardStatValue,
} from '../../../components/Databoard';
import { DeleteModal, DeleteType } from '../../../components/dataBoards/DeleteModal';
import {
	MatterDateFilterDropdown,
	MatterDateFilterProperty,
} from '../../../components/dataBoards/matters/MatterDateFilterDropdown';
import { MatterDetailsFlyout } from '../../../components/dataBoards/matters/MatterDetailsFlyout';
import { MatterPracticeAreaFilterDropdown } from '../../../components/dataBoards/matters/MatterPracticeAreaFilterDropdown';
import { MatterStageFilterDropdown } from '../../../components/dataBoards/matters/MatterStageFilterDropdown';
import { MatterStatusFilterDropdown } from '../../../components/dataBoards/matters/MatterStatusFilterDropdown';
import { EditMatterModal } from '../../../components/dataBoards/matters/EditMatterModal';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../components/DataGrid';
import { DeprecatedSelect, ISelectOption } from '../../../components/DeprecatedSelect';
import { ExportConfirmationModalV2 } from '../../../components/ExportConfirmation';
import { FabContext } from '../../../components/FabContext';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/Popover';
import { CloseButton } from '../../../components/CloseButton';
import { DropDownIcon } from '../../../components/svgs/icons/DropDownIcon';
import { LightningBulbIcon } from '../../../components/svgs/icons/LightBulbIcon';
import { SearchIcon } from '../../../components/svgs/icons/SearchIcon';
import { TextInput } from '../../../components/TextInput';
import { UsersActionBar } from '../../../components/UsersActionBar';
import { ROUTES } from '../../../constants/routes';
import { white } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet as dbStyleSheet } from '../styles';
import { styleSheet } from './styles';

enum MenuItem {
	Edit = 'Edit',
	Delete = 'Delete',
}

enum MatterPropertyOption {
	MatterName = 'Matter Name',
	MatterNumber = 'Matter Number',
}

const MatterPropertyOptions: ISelectOption[] = Object.keys(MatterPropertyOption).map(key => ({
	id: key,
	text: MatterPropertyOption[key as keyof typeof MatterPropertyOption],
	dataContext: key,
}));

export const MattersBoard = () => {
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const history = useHistory();
	const { logApiError } = useEventLogging('MattersBoard');

	// View-only mode for integrations (check both v1 and v2)
	const legalV1IntegrationEnabled = userSession.hasLegalIntegrationConfigured;
	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({ impersonationContext: undefined });
	const legalV2Providers: Api.IntegrationProvider[] = [
		Api.IntegrationProvider.Clio,
		Api.IntegrationProvider.MyCase,
		Api.IntegrationProvider.PracticePanther,
	];
	const legalV2IntegrationEnabled = externalIntegrations?.some(
		integration => integration.enabled && legalV2Providers.includes(integration.provider)
	);
	const canEdit = !legalV1IntegrationEnabled && !legalV2IntegrationEnabled;
	const VIEW_ONLY_KEY = 'ViewOnlyMode_Matters_Alert';
	const [showViewOnlyBanner, setShowViewOnlyBanner] = React.useState(
		() => localStorage.getItem(VIEW_ONLY_KEY) !== 'false'
	);

	const onCloseViewOnlyBanner = () => {
		setShowViewOnlyBanner(false);
		localStorage.setItem(VIEW_ONLY_KEY, 'false');
	};

	// Search/Filter state
	const [selectedSearchProperty, setSelectedSearchProperty] = React.useState(MatterPropertyOptions[0]);
	const [searchFieldValue, setSearchFieldValue] = React.useState('');
	const debouncedSearchFieldValue = useDebounceValue(searchFieldValue, 500);

	// Filter dropdowns state
	const [selectedStages, setSelectedStages] = React.useState<Api.ILegalMatterStage[]>([]);
	const [selectedStatuses, setSelectedStatuses] = React.useState<Api.ILegalMatterStatus[]>([]);
	const [selectedPracticeAreas, setSelectedPracticeAreas] = React.useState<Api.ILegalMatterPracticeArea[]>([]);
	const [selectedDateType, setSelectedDateType] = React.useState<MatterDateFilterProperty>(
		MatterDateFilterProperty.OpenDate
	);
	const [selectedMinimumDate, setSelectedMinimumDate] = React.useState<string>(null);
	const [selectedMaximumDate, setSelectedMaximumDate] = React.useState<string>(null);

	// Modal state
	const [matterToEdit, setMatterToEdit] = React.useState<Partial<Api.ILegalMatter> | null>(null);
	const [matterToDelete, setMatterToDelete] = React.useState<Api.ILegalMatter | null>(null);
	const [isEditOpen, setIsEditOpen] = React.useState(false);
	const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);

	// Flyout state
	const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
	const [matterToView, setMatterToView] = React.useState<Api.ILegalMatter | null>(null);

	const hasSearchValues =
		!!debouncedSearchFieldValue ||
		selectedStages.length > 0 ||
		selectedStatuses.length > 0 ||
		selectedPracticeAreas.length > 0 ||
		!!selectedMinimumDate;

	// Build filter criteria
	const filterRequest = React.useMemo(() => {
		const criteria: Api.ILegalMatterFilterCriteria[] = [];

		if (debouncedSearchFieldValue) {
			if (selectedSearchProperty.id === 'MatterName') {
				criteria.push({ property: 'MatterName', value: debouncedSearchFieldValue });
			} else if (selectedSearchProperty.id === 'MatterNumber') {
				criteria.push({ property: 'MatterNumber', value: debouncedSearchFieldValue });
			}
		}

		// Stage filter - use flat OR structure (like PolicyBoard)
		if (selectedStages.length === 1) {
			criteria.push({ property: 'Stage', value: selectedStages[0].name });
		} else if (selectedStages.length > 1) {
			criteria.push({
				op: 'Or',
				criteria: selectedStages.map(stage => ({ property: 'Stage', value: stage.name })),
			});
		}

		// Status filter - use flat OR structure (like PolicyBoard)
		if (selectedStatuses.length === 1) {
			criteria.push({ property: 'Status', value: selectedStatuses[0].name });
		} else if (selectedStatuses.length > 1) {
			criteria.push({
				op: 'Or',
				criteria: selectedStatuses.map(status => ({ property: 'Status', value: status.name })),
			});
		}

		// Practice area filter - use flat OR structure (like PolicyBoard)
		if (selectedPracticeAreas.length === 1) {
			criteria.push({ property: 'PracticeArea', value: selectedPracticeAreas[0].name });
		} else if (selectedPracticeAreas.length > 1) {
			criteria.push({
				op: 'Or',
				criteria: selectedPracticeAreas.map(pa => ({ property: 'PracticeArea', value: pa.name })),
			});
		}

		// Date range filter - adjust dates by 1 day to make range inclusive (API uses >= and <=)
		if (selectedMinimumDate) {
			const dayBefore = moment(selectedMinimumDate).subtract(1, 'day').format('YYYY-MM-DD');
			const property = selectedDateType === MatterDateFilterProperty.OpenDate ? 'OpenDate' : 'CloseDate';
			criteria.push({ property, value: dayBefore, op: 'Gt' });
		}
		if (selectedMaximumDate) {
			const dayAfter = moment(selectedMaximumDate).add(1, 'day').format('YYYY-MM-DD');
			const property = selectedDateType === MatterDateFilterProperty.OpenDate ? 'OpenDate' : 'CloseDate';
			criteria.push({ property, value: dayAfter, op: 'Lt' });
		}

		return { criteria };
	}, [
		debouncedSearchFieldValue,
		selectedSearchProperty,
		selectedStages,
		selectedStatuses,
		selectedPracticeAreas,
		selectedDateType,
		selectedMinimumDate,
		selectedMaximumDate,
	]);

	// Query hooks
	const matterFilterQuery = useLegalMatterFilter({
		filterRequest,
		pageSize: 25,
		enabled: true,
	});

	const matterSummaryQuery = useLegalMatterSummaryQuery({
		filterRequest,
		enabled: true,
	});

	const exportMattersMutation = useExportLegalMattersMutation({
		onError: error => {
			logApiError('ExportMatters-Error', error);
		},
	});

	const matterContactsMutation = useLegalMatterContactsMutation();

	// Selection state
	const matters = React.useMemo(() => {
		return matterFilterQuery.data?.pages.map(page => page.values).flat() ?? [];
	}, [matterFilterQuery.data?.pages]);

	const {
		excludedRows: excludedMatters,
		selectedRows: selectedMatters,
		selectionState: matterSelectionState,
		isAllSelected,
		isPartialSelected,
		isSelectionStateAll,
		isSelected: isMatterSelected,
		reset: resetMatterSelection,
		toggleRow: toggleMatterRow,
		toggleSelectAll: toggleSelectAllMatters,
	} = useSelectionState({ rows: matters?.map(m => m.id).filter((id): id is string => !!id) });

	const [actionButtonsDisabled, setActionButtonsDisabled] = React.useState(false);

	const refreshMatters = () => {
		invalidateAllLegalMatterFilters();
		invalidateAllLegalMatterSummary();
	};

	// Deduplicate contacts - a contact may have multiple matters
	const dedupeContacts = (contacts: Api.IProjectedContact[]) => {
		return contacts.filter((contact, index) => contacts.findIndex(c => c.id === contact.id) === index);
	};

	const excludedContacts = React.useMemo(() => {
		return dedupeContacts(
			excludedMatters
				.map(matterId => {
					const matter = matters.find(m => m.id === matterId);
					return matter?.contact ? matter.contact : null;
				})
				.filter((contact): contact is Api.IProjectedContact => contact != null)
		);
	}, [excludedMatters, matters]);

	const selectedContacts = React.useMemo(() => {
		return dedupeContacts(
			matters
				.filter(matter => matter.id && selectedMatters.indexOf(matter.id) > -1)
				.map(matter => matter.contact)
				.filter((contact): contact is Api.IProjectedContact => contact != null)
		);
	}, [matters, selectedMatters]);

	const getMatchingContacts = async () => {
		setActionButtonsDisabled(true);

		let results: Api.IProjectedContact[] = [];

		if (isAllSelected) {
			try {
				const excludedContactsCriteria = excludedContacts
					.filter((contact): contact is Api.IProjectedContact & { id: string } => !!contact.id)
					.map(contact => ({
						property: 'ContactId',
						op: 'Not' as const,
						value: contact.id,
					}));
				const apiResults = await matterContactsMutation.mutateAsync({
					filterRequest: {
						criteria: [...(filterRequest.criteria || []), ...excludedContactsCriteria],
					},
				});

				const excludedContactIdsMap = new Set(excludedContacts.map(contact => contact.id));
				results = apiResults.filter(contact => !excludedContactIdsMap.has(contact.id));
			} catch (error) {
				logApiError('ResolveContacts-Error', error);
			}
		} else {
			results = dedupeContacts(
				matters
					.filter(matter => matter.id && selectedMatters.indexOf(matter.id) > -1)
					.map(matter => matter.contact)
					.filter((contact): contact is Api.IProjectedContact => contact != null)
			);
		}

		setActionButtonsDisabled(false);
		return results;
	};

	const onExport = () => {
		exportMattersMutation.mutate({
			filterRequest: {
				...filterRequest,
				...(matterSelectionState === Api.ESelectionState.Some
					? { includeIds: selectedMatters }
					: matterSelectionState === Api.ESelectionState.All
						? { excludeIds: excludedMatters }
						: {}),
			},
		});
	};

	const clearSearch = () => {
		setSelectedSearchProperty(MatterPropertyOptions[0]);
		setSearchFieldValue('');
		setSelectedStages([]);
		setSelectedStatuses([]);
		setSelectedPracticeAreas([]);
		setSelectedMinimumDate(null);
		setSelectedMaximumDate(null);
		invalidateAllLegalMatterFilters();
		invalidateAllLegalMatterSummary();
	};

	const onSearchFieldInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchFieldValue(e.target.value);
	};

	const selectSearchProperty = (selectedOption: ISelectOption<MatterPropertyOption>) => {
		setSearchFieldValue('');
		setSelectedSearchProperty(selectedOption);
	};

	const addMatterClicked = () => {
		setMatterToEdit({});
		setIsEditOpen(true);
	};

	const viewMatterDetails = (matter: Api.ILegalMatter) => {
		setMatterToView(matter);
		setIsDetailsOpen(true);
	};

	const editMatter = (matter: Api.ILegalMatter) => {
		setMatterToEdit(matter);
		setIsEditOpen(true);
	};

	const deleteMatter = (matter: Api.ILegalMatter) => {
		setMatterToDelete(matter);
		setIsDeleteOpen(true);
	};

	const onDeleteConfirmed = () => {
		setIsDeleteOpen(false);
		setMatterToDelete(null);
		refreshMatters();
	};

	const onEditSave = () => {
		setIsEditOpen(false);
		setMatterToEdit(null);
		refreshMatters();
	};

	const onEditCancel = () => {
		setIsEditOpen(false);
		setMatterToEdit(null);
	};

	const onSendEmailClicked = async () => {
		const matchingContacts = await getMatchingContacts();

		fullscreenModal.history?.push({
			pathname: '/email/campaigns/create/from-selection',
			state: {
				model: {
					context: {
						allowSenderSelection: true,
						bulkFilterRequestOptions: {
							readOnlySearchCriteria: true,
						},
						contacts: matchingContacts.map(c => new Api.ContactViewModel(userSession, c)),
					},
					type: 'Contacts',
				},
			},
		});
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return '-';
		return moment(dateString).format('MM/DD/YYYY');
	};

	const isLoading = matterFilterQuery.isPending;
	const noResultsFound = !isLoading && matterFilterQuery.isFetched && matters.length === 0;

	return (
		<Page>
			<MultiContainerHeader
				appBarHeader={
					<BreadcrumbBar
						items={[
							{ label: 'Data Boards', path: ROUTES.dataBoards.home },
							{ label: 'Matters', path: ROUTES.dataBoards.matters },
						]}
					/>
				}
			/>

			<Column styles={[styleSheet.container]}>
				<Databoard styles={[styleSheet.databoardContainer]}>
					{!canEdit && showViewOnlyBanner ? (
						<div className={css(styleSheet.viewOnlyModeBannerContainer)}>
							<div className={css(styleSheet.viewOnlyModeBannerContent)}>
								<LightningBulbIcon />
								<div>
									<h4 className={css(styleSheet.infoHeaderContentBold)}>View Only Mode</h4>
									<div>When a legal integration is enabled you cannot make edits to matters data through Levitate.</div>
								</div>
							</div>
							<CloseButton onClick={onCloseViewOnlyBanner} />
						</div>
					) : null}

					<DataboardFilters>
						<div className={css(dbStyleSheet.propertySearch)}>
							<DeprecatedSelect
								styles={[dbStyleSheet.searchPropertySelectorResponsive]}
								onOptionClick={selectedOption => selectSearchProperty(selectedOption)}
								options={MatterPropertyOptions}
								selectedOption={selectedSearchProperty}
							/>

							<TextInput
								inputId='matter-search-input'
								type='text'
								className={css(dbStyleSheet.comboBoxRightAutocompleteResponsive)}
								inputClassName={css(dbStyleSheet.wideInputResponsive)}
								onChange={onSearchFieldInputChanged}
								value={searchFieldValue}
								leftAccessory={<SearchIcon className={css(dbStyleSheet.searchIcon)} />}
								placeholder={`Search by ${selectedSearchProperty.text}...`}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<MatterStageFilterDropdown
								selectedStages={selectedStages}
								setSelectedStages={setSelectedStages}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<MatterStatusFilterDropdown
								selectedStatuses={selectedStatuses}
								setSelectedStatuses={setSelectedStatuses}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<MatterPracticeAreaFilterDropdown
								selectedPracticeAreas={selectedPracticeAreas}
								setSelectedPracticeAreas={setSelectedPracticeAreas}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						<div className={css(dbStyleSheet.mediumInput)}>
							<MatterDateFilterDropdown
								selectedDateType={selectedDateType}
								setSelectedDateType={setSelectedDateType}
								selectedMinimumDate={selectedMinimumDate}
								setSelectedMinimumDate={setSelectedMinimumDate}
								selectedMaximumDate={selectedMaximumDate}
								setSelectedMaximumDate={setSelectedMaximumDate}
								anchorClassName={css(dbStyleSheet.dropdownReset)}
							/>
						</div>

						{hasSearchValues && (
							<div className={css(dbStyleSheet.clearSearch)}>
								<button
									className={css(baseStyleSheet.ctaButtonReverse, dbStyleSheet.clearSearchButton)}
									onClick={clearSearch}
								>
									<span>Clear Search</span>
								</button>
							</div>
						)}
					</DataboardFilters>

					<DataboardActions>
						<div className={css(dbStyleSheet.leftHeader)}>
							<UsersActionBar
								excludedContacts={excludedContacts}
								selectedContacts={selectedContacts}
								getMatchingContacts={getMatchingContacts}
								onExport={onExport}
								selectionState={matterSelectionState}
								actionButtonsDisabled={actionButtonsDisabled}
								onSendEmailClicked={onSendEmailClicked}
								showKeepInTouch={false}
							/>
						</div>

						{canEdit ? (
							<div className={css(dbStyleSheet.rightHeader)}>
								<div className={css(dbStyleSheet.addButtonContainer)}>
									<button className={css(baseStyleSheet.ctaButton, dbStyleSheet.addButton)} onClick={addMatterClicked}>
										<span>Add Matter</span>
									</button>
									<Popover>
										<PopoverTrigger asChild>
											<button className={css(dbStyleSheet.dropdownCaret)}>
												<DropDownIcon fillColor={white} />
											</button>
										</PopoverTrigger>
										<PopoverContent align='end' styles={[styleSheet.addMatterBtnPopover]}>
											<button
												className={css(baseStyleSheet.ctaButtonReverse, dbStyleSheet.importButton)}
												onClick={() => {
													history.push(ROUTES.dataBoards.mattersImport);
												}}
											>
												Import from CSV
											</button>
										</PopoverContent>
									</Popover>
								</div>
							</div>
						) : null}
					</DataboardActions>

					<DataboardBody data-scroll-restoration-id='matters-page-table' hasFooter>
						<DataGrid styles={[dbStyleSheet.tableOverrides]} isLoading={isLoading}>
							<DataGridColumns>
								<DataGridColumn sticky styles={[dbStyleSheet.checkboxColumn]}>
									<Checkbox
										checked={isSelectionStateAll}
										id='select-all-matters'
										onChange={() => {
											resetMatterSelection();
											toggleSelectAllMatters();
										}}
										partial={isPartialSelected}
									/>
								</DataGridColumn>
								<DataGridColumn sticky>Contact</DataGridColumn>
								<DataGridColumn sticky>Matter Name</DataGridColumn>
								<DataGridColumn sticky>Matter Number</DataGridColumn>
								<DataGridColumn sticky>Stage</DataGridColumn>
								<DataGridColumn sticky>Status</DataGridColumn>
								<DataGridColumn sticky>Practice Area</DataGridColumn>
								<DataGridColumn sticky>Open Date</DataGridColumn>
								<DataGridColumn sticky>Close Date</DataGridColumn>
								<DataGridColumn sticky>&nbsp;</DataGridColumn>
							</DataGridColumns>

							<DataGridRows>
								{!isLoading
									? matters.map(matter => (
											<DataGridRow key={`matter-${matter.id}`} styles={[dbStyleSheet.row]}>
												<DataGridRowItem>
													<Checkbox
														readOnly
														checked={isMatterSelected(matter.id ?? '')}
														id={matter.id ?? ''}
														onClick={event => {
															event.stopPropagation();
															event.preventDefault();
															if (matter.id) {
																toggleMatterRow(matter.id, event.shiftKey);
															}
														}}
													/>
												</DataGridRowItem>
												<DataGridRowItem>
													{matter.contact ? (
														<EntityChip
															className={css(dbStyleSheet.contact)}
															key={matter.contact.id}
															entity={new Api.ContactViewModel(userSession, matter.contact)}
															readOnly={true}
															sizeConstraint='compact'
														/>
													) : null}
												</DataGridRowItem>
												<DataGridRowItem>
													<button onClick={() => viewMatterDetails(matter)}>
														<h6 className={css(dbStyleSheet.name, baseStyleSheet.truncateText)}>{matter.matterName}</h6>
													</button>
												</DataGridRowItem>
												<DataGridRowItem>{matter.matterNumber}</DataGridRowItem>
												<DataGridRowItem>{matter.stage?.name || '-'}</DataGridRowItem>
												<DataGridRowItem>{matter.status?.name || '-'}</DataGridRowItem>
												<DataGridRowItem>{matter.practiceArea?.name || '-'}</DataGridRowItem>
												<DataGridRowItem>{formatDate(matter.openDate)}</DataGridRowItem>
												<DataGridRowItem>{formatDate(matter.closeDate)}</DataGridRowItem>
												<DataGridRowItem>
													{canEdit && matter.canModify ? (
														<Dropdown>
															<DropdownEllipsisTrigger />
															<DropdownContent>
																<DropdownItem onClick={() => editMatter(matter)}>{MenuItem.Edit}</DropdownItem>
																<DropdownItem onClick={() => deleteMatter(matter)}>{MenuItem.Delete}</DropdownItem>
															</DropdownContent>
														</Dropdown>
													) : null}
												</DataGridRowItem>
											</DataGridRow>
										))
									: null}

								{isLoading ? (
									<DataGridRow>
										<DataGridRowItem colSpan={10}>
											<LoadingSpinner type='large' />
										</DataGridRowItem>
									</DataGridRow>
								) : null}
							</DataGridRows>
						</DataGrid>

						{!matterFilterQuery.isPending && matterFilterQuery.hasNextPage ? (
							<Waypoint bottomOffset='-200px' onEnter={() => matterFilterQuery.fetchNextPage()} />
						) : null}

						{noResultsFound ? <DataboardNoResults onClearSearch={clearSearch} /> : null}
					</DataboardBody>

					<DataboardStats>
						<DataboardStatTitle>Matter Stats</DataboardStatTitle>
						<DataboardStat>
							<DataboardStatLabel>Total Matters</DataboardStatLabel>
							<DataboardStatValue>{matterSummaryQuery.data?.totalMatters || 0}</DataboardStatValue>
						</DataboardStat>

						<DataboardStat>
							<DataboardStatLabel>Open Matters</DataboardStatLabel>
							<DataboardStatValue>{matterSummaryQuery.data?.openMatters || 0}</DataboardStatValue>
						</DataboardStat>

						<DataboardStat>
							<DataboardStatLabel>Closed Matters</DataboardStatLabel>
							<DataboardStatValue>{matterSummaryQuery.data?.closedMatters || 0}</DataboardStatValue>
						</DataboardStat>
					</DataboardStats>

					{matterToEdit ? (
						<EditMatterModal
							initialMatter={matterToEdit}
							isOpen={isEditOpen}
							setIsOpen={setIsEditOpen}
							onSave={onEditSave}
							onCancel={onEditCancel}
						/>
					) : null}

					{matterToDelete ? (
						<DeleteModal
							type={DeleteType.Matter}
							item={matterToDelete}
							isOpen={isDeleteOpen}
							setIsOpen={setIsDeleteOpen}
							onConfirm={onDeleteConfirmed}
						/>
					) : null}

					{matterToView ? (
						<MatterDetailsFlyout isOpen={isDetailsOpen} setIsOpen={setIsDetailsOpen} matter={matterToView} />
					) : null}

					<ExportConfirmationModalV2
						mutationResult={exportMattersMutation}
						modalProps={{
							isOpen: !exportMattersMutation.isIdle,
							onRequestClose: () => exportMattersMutation.reset(),
						}}
						subTitle={
							<>
								<p className={css(dbStyleSheet.noMargin)}>
									The fields we&apos;ll export include: Contact Name, Matter Name, Matter Number, Stage, Status,
									Practice Area, Open Date, Close Date
								</p>
							</>
						}
					/>

					<FabContext appearance={{ hidden: true }} />
				</Databoard>
			</Column>
		</Page>
	);
};
